// Modelbeleid: de enige plek waar modellen gekozen worden. Nooit Haiku. Nieuwere modellen in
// dezelfde familie winnen automatisch (opwaardering via de modellenlijst van de API).

export type ModelTier = 'standaard' | 'zwaar' | 'licht';
export type EffortLevel = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export const FALLBACK_MODELS: Record<ModelTier, string> = {
  standaard: 'claude-opus-5',
  zwaar: 'claude-fable-5-1',
  licht: 'claude-sonnet-5',
};

export const TIER_FAMILY: Record<ModelTier, string> = { standaard: 'opus', zwaar: 'fable', licht: 'sonnet' };
const FAMILIES = new Set(Object.values(TIER_FAMILY));

export const EFFORT_ORDER: readonly EffortLevel[] = ['low', 'medium', 'high', 'xhigh', 'max'];

/** 'claude-opus-5' → opus [5]; 'claude-fable-5-1' → fable [5,1]; een datumsuffix (8 cijfers) telt niet mee; haiku en onbekende vormen → null. */
export function parseModelId(id: string): { family: string; version: number[] } | null {
  const m = /^claude-([a-z]+)-(\d+(?:-\d+)*)$/.exec(id);
  if (!m || !FAMILIES.has(m[1]!)) return null;
  const version = m[2]!.split('-').map(Number).filter((n) => n < 1000);
  return { family: m[1]!, version };
}

function compare(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export function pickLatest(family: string, ids: string[]): string | undefined {
  let best: { id: string; version: number[] } | undefined;
  for (const id of ids) {
    const p = parseModelId(id);
    if (!p || p.family !== family) continue;
    if (!best || compare(p.version, best.version) > 0) best = { id, version: p.version };
  }
  return best?.id;
}

export function assertAllowedModel(id: string): void {
  if (/haiku/i.test(id)) throw new Error(`Haiku is niet toegestaan (${id})`);
}

/** Bepaalt per tier het model: pin > nieuwste uit de API > vaste terugval. Gooit bij Haiku. */
export async function resolveModels(
  listIds: () => Promise<string[]>,
  pins: Partial<Record<ModelTier, string>> = {},
  warn: (message: string) => void = () => undefined,
): Promise<Record<ModelTier, string>> {
  for (const pin of Object.values(pins)) if (pin) assertAllowedModel(pin);
  let ids: string[] = [];
  try {
    ids = await listIds();
  } catch (e) {
    warn(`modellen ophalen mislukt, terugval op vaste lijst: ${(e as Error).message}`);
  }
  const out = { ...FALLBACK_MODELS };
  for (const tier of Object.keys(out) as ModelTier[]) {
    out[tier] = pins[tier] ?? pickLatest(TIER_FAMILY[tier], ids) ?? FALLBACK_MODELS[tier];
    assertAllowedModel(out[tier]);
  }
  return out;
}

export function atLeastEffort(effort: EffortLevel, floor: EffortLevel): EffortLevel {
  return EFFORT_ORDER.indexOf(effort) < EFFORT_ORDER.indexOf(floor) ? floor : effort;
}
